import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/layout/Breadcrumbs";
import SectionHeading from "../components/blocks/SectionHeading";
import CTASection from "../components/blocks/CTASection";
import { CalendarDays, MapPin, Clock } from "lucide-react";

const EVENTS = [
  {
    slug: "strategic-thinking-open-masterclass",
    type: "Open Masterclass",
    status: "Dates to be announced",
    title: "Strategic Thinking for Senior Leaders — Open Masterclass",
    description: "A working masterclass on seeing the whole board: industry dynamics, competitive position and the choices that create advantage. Participants apply the frameworks to their own live situations.",
    dates: "Season One — dates announced shortly",
    format: "In-person",
    audience: "Senior leaders, founders and business owners",
  },
  {
    slug: "executive-briefing-capability",
    type: "Executive Briefing",
    status: "Dates to be announced",
    title: "The Executive Briefing: Building Capability That Moves the Business",
    description: "A concise briefing for leadership teams and HR/L&D leaders on why capability — not content — is the constraint, and how to design development that changes what the business can do.",
    dates: "Season One — dates announced shortly",
    format: "Online, live",
    audience: "CXOs, HR and L&D leaders",
  },
  {
    slug: "toolbox-live-first-principles",
    type: "Framework Webinar",
    status: "Dates to be announced",
    title: "Thinking Toolbox™ Live: First Principles in Practice",
    description: "A live walk-through of First Principles Thinking: the logic, the business application and a guided exercise on a real problem. First in a series covering the Toolbox's thirteen frameworks.",
    dates: "Season One — dates announced shortly",
    format: "Online, live",
    audience: "Leaders, managers and professionals",
  },
];

export default function EventsPage() {
  return (
    <>
      <SEO
        title="Events — Masterclasses, Briefings & Webinars | The Vantage Institute™"
        description="Upcoming Vantage Institute events: executive masterclasses, leadership briefings, framework sessions and webinars. The events calendar is being finalised."
        path="/events"
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Events" }]} />

      <section className="bg-navy">
        <div className="container-x py-16 sm:py-20">
          <p className="eyebrow-light">Events</p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-semibold leading-tight text-white sm:text-5xl">
            Vantage events and briefings.
          </h1>
          <span className="gold-rule mt-8 !w-24" aria-hidden="true" />
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-platinum md:text-lg">
            Executive masterclasses, leadership briefings, framework sessions and webinars — in person and online.
          </p>
        </div>
      </section>

      <section className="container-x py-16 sm:py-24" data-testid="events-list">
        <SectionHeading
          eyebrow="Season One"
          title="The first Vantage events series."
          intro="Dates and venues are being finalised and will be announced here. Register your interest and the Institute will contact you when booking opens."
        />
        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {EVENTS.map((e) => (
            <article key={e.slug} className="card card-hover flex h-full flex-col" data-testid={`event-card-${e.slug}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="bg-navy px-2.5 py-1 font-sub text-[0.65rem] font-semibold uppercase tracking-wider text-white">
                  {e.type}
                </span>
                <span className="border border-gold/50 px-2.5 py-1 font-sub text-[0.65rem] font-semibold uppercase tracking-wider text-gold-deep">
                  {e.status}
                </span>
              </div>
              <h3 className="mt-4 font-sub text-base font-semibold leading-snug text-navy">{e.title}</h3>
              <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate">{e.description}</p>
              <dl className="mt-4 space-y-1.5 border-t border-platinum pt-4 text-xs text-slate">
                <div className="flex items-center gap-2"><CalendarDays size={14} aria-hidden="true" className="text-gold-deep" /><dt className="sr-only">Dates</dt><dd>{e.dates}</dd></div>
                <div className="flex items-center gap-2"><MapPin size={14} aria-hidden="true" className="text-gold-deep" /><dt className="sr-only">Format</dt><dd>{e.format}</dd></div>
                <div className="flex items-center gap-2"><Clock size={14} aria-hidden="true" className="text-gold-deep" /><dt className="sr-only">Audience</dt><dd>{e.audience}</dd></div>
              </dl>
              <Link to="/contact" className="text-link mt-5 inline-flex min-h-[44px] items-center" data-testid={`event-card-${e.slug}-cta`}>
                Register Your Interest
              </Link>
            </article>
          ))}
        </div>
        <p className="mt-8 max-w-2xl text-sm leading-relaxed text-slate">
          To be notified when dates are announced, or to discuss a private briefing for your leadership team,{" "}
          <Link to="/contact" className="text-link" data-testid="events-contact-link">talk to Vantage</Link>.
        </p>
      </section>

      <CTASection
        title="Bring a Vantage briefing to your organisation."
        text="Private executive briefings and framework sessions can be arranged for leadership teams through a corporate conversation."
        primaryLabel="Request a Corporate Conversation"
        primaryTo="/organisations#enquiry"
        secondaryLabel="Talk to Vantage"
        secondaryTo="/contact"
        testId="events-cta"
      />
    </>
  );
}
